import { lowerCase } from "lodash-es";
import { useIntl } from "react-intl";

import {
  IconCarryOnBaggage,
  IconCarryOnBaggageNotIncluded,
  IconCheckedBaggage,
  IconCheckedBaggageNotIncluded,
  IconHandBag,
  IconHandBagNotIncluded,
} from "@/components/Icons";

import type {
  ExtractedFlightFareDetails,
  ExtractedFlightFareDetailsBaggageItem,
} from "../extractDataFromFlightFare";
import {
  FlightFareCardSection,
  FlightFareCardSectionItem,
} from "../FlightFareCardSection";

type FlightFareBaggageInfoProps = {
  baggage: NonNullable<ExtractedFlightFareDetails["baggage"]>;
};

export function FlightFareBaggageInfo({ baggage }: FlightFareBaggageInfoProps) {
  const intl = useIntl();

  return (
    <FlightFareCardSection
      title={intl.formatMessage({
        defaultMessage: "Carry-on Baggage",
        description: "Section title for baggage information in fare card",
      })}
    >
      <FlightFareBaggageInfoItem
        baggageName={intl.formatMessage({
          defaultMessage: "Carry-on baggage",
          description: "Label for carry-on baggage information in fare card",
        })}
        baggageItem={baggage.carryOn}
        icon={<IconCarryOnBaggage className="size-6" />}
        notIncludedIcon={<IconCarryOnBaggageNotIncluded className="size-6" />}
      />

      <FlightFareBaggageInfoItem
        baggageName={intl.formatMessage({
          defaultMessage: "Checked baggage",
          description: "Label for checked baggage information in fare card",
        })}
        baggageItem={baggage.checked}
        icon={<IconCheckedBaggage className="size-6" />}
        notIncludedIcon={<IconCheckedBaggageNotIncluded className="size-6" />}
      />

      <FlightFareBaggageInfoItem
        baggageName={intl.formatMessage({
          defaultMessage: "Hand bag",
          description: "Label for hand bag baggage information in fare card",
        })}
        baggageItem={baggage.smallBag}
        icon={<IconHandBag className="size-6" />}
        notIncludedIcon={<IconHandBagNotIncluded className="size-6" />}
      />
    </FlightFareCardSection>
  );
}

type FlightFareBaggageInfoItemProps = {
  baggageName: string;
  baggageItem: ExtractedFlightFareDetailsBaggageItem | null;
  icon: React.ReactNode;
  notIncludedIcon: React.ReactNode;
};

function FlightFareBaggageInfoItem({
  baggageName,
  baggageItem,
  icon,
  notIncludedIcon,
}: FlightFareBaggageInfoItemProps) {
  const intl = useIntl();

  if (!baggageItem) {
    return (
      <FlightFareCardSectionItem
        label={intl.formatMessage(
          {
            defaultMessage: "No {baggageName}",
            description:
              "Label for baggage information in fare card when no baggage is allowed for this baggage name",
          },
          { baggageName: lowerCase(baggageName) },
        )}
        icon={notIncludedIcon}
      />
    );
  }

  let label: string;
  if (baggageItem.weight) {
    label = intl.formatMessage(
      {
        defaultMessage: "{baggageName}: {weight}kg",
        description:
          "Label for baggage information in fare card showing the weight limit of the baggage",
      },
      {
        baggageName: lowerCase(baggageName),
        weight: baggageItem.weight,
      },
    );
  } else {
    label = intl.formatMessage(
      {
        defaultMessage: "{baggageName}: Included",
        description:
          "Label for baggage information in fare card when baggage is included but no weight information is provided",
      },
      { baggageName: lowerCase(baggageName) },
    );
  }

  return (
    <FlightFareCardSectionItem
      label={label}
      description={
        baggageItem.dimensions
          ? intl.formatMessage(
              {
                defaultMessage: "up to {dimensions} cm",
                description:
                  "Description for baggage information in fare card showing dimensions limits",
              },
              {
                dimensions: baggageItem.dimensions,
              },
            )
          : undefined
      }
      icon={icon}
      suffix={
        baggageItem.count
          ? intl.formatMessage(
              {
                defaultMessage: "x{count} per adult",
                description:
                  "Suffix for baggage information in fare card showing the number of baggage allowed per adult",
              },
              {
                count: baggageItem.count,
              },
            )
          : undefined
      }
    />
  );
}
